/**
 * Read-only collapsible tree of linked KPIs (CEO → role → team → individual).
 */
// React imports
import React, { useEffect, useState } from "react";

// External library imports
import { Box, Button, Chip, Collapse, Typography } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";

/**
 * A single node in the hierarchical KPI tree
 * (CEO KPI -> Department/Role KPIs -> Team KPIs -> Individual KPIs).
 */
export interface KPITreeNode {
  kpi_id: string;
  kpi_code?: string | null;
  title: string;
  designation_name?: string | null;
  threshold?: number | null;
  weight?: number;
  linkage_id?: string;
  children: KPITreeNode[];
}

/** Props for the KPI hierarchy tree. */
interface KPITreeViewProps {
  nodes: KPITreeNode[];
  onSelectNode?: (node: KPITreeNode) => void;
  selectedKpiId?: string | null;
}

const TREE_GREEN = "#2e7d32";
const GUIDE = "#e0e0e0";

/** Format a linkage weight for display with two decimal places. */
const formatWeight = (value: number): string => Number(value).toFixed(2);

/** Small chip for weight or threshold next to a tree node. */
const MetaPill: React.FC<{ label: string; kind?: "weight" | "threshold" }> = ({
  label,
  kind = "weight",
}) => (
  <Chip
    size="small"
    label={label}
    variant={kind === "threshold" ? "outlined" : "filled"}
    sx={{
      height: 24,
      fontSize: "0.75rem",
      fontWeight: kind === "threshold" ? 600 : 500,
      bgcolor: kind === "threshold" ? "#e3f2fd" : "#f0f0f0",
      color: kind === "threshold" ? "#1565c0" : "#424242",
      border: kind === "threshold" ? "2px solid #1976d2" : "none",
      "& .MuiChip-label": { px: 1.25 },
    }}
  />
);

/** One expandable row in the KPI tree. */
const KPITreeNodeItem: React.FC<{
  node: KPITreeNode;
  depth: number;
  expandVersion: number;
  expandOpen: boolean;
  onSelectNode?: (node: KPITreeNode) => void;
  selectedKpiId?: string | null;
}> = ({ node, depth, expandVersion, expandOpen, onSelectNode, selectedKpiId }) => {
  const [open, setOpen] = useState<boolean>(depth < 2);
  const hasChildren = Boolean(node.children && node.children.length > 0);
  const selected = selectedKpiId === node.kpi_id;
  const isRoot = depth === 0 && node.weight === undefined;

  useEffect(() => {
    if (expandVersion === 0) return;
    setOpen(expandOpen);
  }, [expandVersion, expandOpen]);

  const toggle = () => {
    if (hasChildren) setOpen((prev) => !prev);
    onSelectNode?.(node);
  };

  return (
    <Box>
      <Box
        onClick={toggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 0.85,
          pr: 1,
          pl: 0.5,
          borderRadius: 1,
          cursor: "pointer",
          bgcolor: selected ? "#f5f5f5" : "transparent",
          "&:hover": { bgcolor: "#fafafa" },
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: hasChildren ? TREE_GREEN : "#bdbdbd",
          }}
        >
          {hasChildren ? (
            open ? (
              <ArrowDropDownIcon fontSize="small" />
            ) : (
              <ArrowRightIcon fontSize="small" />
            )
          ) : (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#bdbdbd",
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            px: 0.85,
            py: 0.15,
            borderRadius: 0.75,
            bgcolor: "#eceff1",
            color: "#546e7a",
            fontSize: "0.72rem",
            fontWeight: 600,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            lineHeight: 1.6,
            flexShrink: 0,
          }}
        >
          {node.kpi_code || node.kpi_id.slice(0, 8)}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: "0.9rem",
              color: "#212121",
              lineHeight: 1.4,
            }}
          >
            {node.title}
          </Typography>
          {node.designation_name && (
            <Chip
              size="small"
              label={node.designation_name}
              variant="outlined"
              sx={{
                height: 24,
                fontSize: "0.75rem",
                fontWeight: 600,
                color: TREE_GREEN,
                borderColor: TREE_GREEN,
                bgcolor: "transparent",
                "& .MuiChip-label": { px: 1.1 },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0, ml: 1 }}>
          {isRoot && node.threshold != null && (
            <MetaPill kind="threshold" label={`Threshold ${node.threshold}`} />
          )}
          {node.weight !== undefined && (
            <MetaPill kind="weight" label={`Weight ${formatWeight(node.weight)}`} />
          )}
        </Box>
      </Box>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box
            sx={{
              ml: 1.4,
              pl: 1.6,
              borderLeft: `1px solid ${GUIDE}`,
            }}
          >
            {node.children.map((child, index) => (
              <KPITreeNodeItem
                key={`${child.linkage_id || child.kpi_id}-${index}`}
                node={child}
                depth={depth + 1}
                expandVersion={expandVersion}
                expandOpen={expandOpen}
                onSelectNode={onSelectNode}
                selectedKpiId={selectedKpiId}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

/**
 * Collapsible KPI hierarchy tree. Structure is read-only here — weights
 * are edited on the Manage Linkages tab.
 */
const KPITreeView: React.FC<KPITreeViewProps> = ({ nodes, onSelectNode, selectedKpiId }) => {
  const [expandVersion, setExpandVersion] = useState(0);
  const [expandOpen, setExpandOpen] = useState(true);

  const expandAll = () => {
    setExpandOpen(true);
    setExpandVersion((v) => v + 1);
  };

  const collapseAll = () => {
    setExpandOpen(false);
    setExpandVersion((v) => v + 1);
  };

  if (!nodes || nodes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No linkages yet. Create a linkage in the Manage Linkages tab to start building the hierarchy.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UnfoldLessIcon />}
          onClick={collapseAll}
        >
          Collapse All
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<UnfoldMoreIcon />}
          onClick={expandAll}
        >
          Expand All
        </Button>
      </Box>
      {nodes.map((node) => (
        <KPITreeNodeItem
          key={node.kpi_id}
          node={node}
          depth={0}
          expandVersion={expandVersion}
          expandOpen={expandOpen}
          onSelectNode={onSelectNode}
          selectedKpiId={selectedKpiId}
        />
      ))}
    </Box>
  );
};

export default KPITreeView;
